<template>
    <div v-if="data" class="stl-reconcile-detail">
        <div class="stl-rc-grid">
            <!-- 세대 -->
            <section class="stl-rc-block">
                <div class="stl-rc-block-h">
                    <span class="tag s">세대</span>
                    <div class="meta">
                        <strong>{{ sourcesS.label || "세대 합계" }}</strong>
                        <span class="microcap dim">{{ sourcesS.method }}</span>
                    </div>
                    <span class="val num"
                        >{{ fmtUsage(data.subUsageSumQty) }}<small>{{ unitText }}</small></span
                    >
                </div>
                <div class="stl-rc-block-b microcap dim">
                    입주 {{ sourcesS.householdCount ?? "—" }}세대
                    <template v-if="sourcesS.totalRegistered != null">
                        / 등록 {{ sourcesS.totalRegistered }}세대</template
                    >
                    <template v-if="data.expectedDayCount"> · 집계 {{ data.expectedDayCount }}일</template>
                </div>
            </section>

            <!-- 공용 (전기) -->
            <section v-if="!isGas && sourcesC" class="stl-rc-block">
                <div class="stl-rc-block-h">
                    <span class="tag c">공용</span>
                    <div class="meta">
                        <strong>{{ sourcesC.label || L.commonMetering }}</strong>
                        <span class="microcap dim">{{ sourcesC.sysNodeName }} · #{{ sourcesC.sysNodeIdx }}</span>
                    </div>
                    <span class="val num"
                        >{{ fmtUsage(data.commonUsageQty) }}<small>{{ unitText }}</small></span
                    >
                </div>
                <div class="stl-rc-block-b microcap">
                    <template v-if="sourcesC.ptAddr">
                        {{ sourcesC.ptAddr }}<span class="dim"> ({{ sourcesC.savePtIdx }})</span>
                    </template>
                    <span v-else class="nodeid">({{ sourcesC.savePtIdx }})</span>
                    <span v-if="sourcesC.dayCount != null" class="dim">
                        · {{ sourcesC.dayCount }}/{{ data.expectedDayCount }}일</span
                    >
                </div>
            </section>

            <!-- 전체 -->
            <section class="stl-rc-block stl-rc-block--muted">
                <div class="stl-rc-block-h">
                    <span class="tag m">전체</span>
                    <div class="meta">
                        <strong>{{ sourcesM.label || L.settlementTotal }}</strong>
                        <span class="microcap dim">{{ sourcesM.note || L.notIndependentMeter }}</span>
                    </div>
                    <span class="val num"
                        >{{ fmtUsage(data.masterUsageQty) }}<small>{{ unitText }}</small></span
                    >
                </div>
                <div class="stl-rc-block-b microcap dim">{{ sourcesM.method }} (정산)</div>
            </section>

            <!-- 한전 대조 -->
            <section
                v-if="hasKepco"
                class="stl-rc-block"
                :class="kepcoMatch ? 'stl-rc-block--ok' : 'stl-rc-block--warn'"
            >
                <div class="stl-rc-block-h">
                    <span class="tag k">한전</span>
                    <div class="meta">
                        <strong>고지 사용량 대조</strong>
                        <span class="microcap dim">{{ L.kepcoCustNo }} {{ data.kepcoCustNo }}</span>
                    </div>
                    <span class="val num"
                        >{{ fmtUsage(data.kepcoKwh) }}<small>{{ unitText }}</small></span
                    >
                </div>
                <div class="stl-rc-block-b">
                    <span class="microcap">{{ L.compositeVsKepco }} </span>
                    <span class="num" :class="kepcoDeltaClass">{{ kepcoDeltaText }}</span>
                    <span class="stl-chip" :class="kepcoMatch ? 'ok' : 'warn'" style="margin-left: 6px">
                        {{ kepcoMatch ? "근접" : "차이" }}
                    </span>
                </div>
            </section>

            <!-- 정산 스냅샷 -->
            <section v-if="snapshotUsage != null" class="stl-rc-block stl-rc-block--muted">
                <div class="stl-rc-block-h">
                    <span class="tag snap">정산</span>
                    <div class="meta">
                        <strong>정산 스냅샷</strong>
                        <span v-if="matrixStatus" class="microcap dim">{{ statusLabel(matrixStatus) }}</span>
                    </div>
                    <span class="val num"
                        >{{ fmtUsage(snapshotUsage) }}<small>{{ unitText }}</small></span
                    >
                </div>
                <div v-if="snapshotDelta != null" class="stl-rc-block-b microcap dim">
                    {{ L.realtimeVsSnapshot }} {{ fmtUsage(snapshotDelta) }} {{ unitText }}
                </div>
            </section>
        </div>

        <!-- 세대별 -->
        <section v-if="rows.length" class="stl-rc-households">
            <button type="button" class="stl-rc-toggle" @click="expanded = !expanded">
                <i class="fa" :class="expanded ? 'fa-chevron-down' : 'fa-chevron-right'"></i>
                {{ L.reconcileHouseholdPt }} ({{ rows.length }}세대)
            </button>
            <div v-show="expanded" class="stl-rc-table-wrap">
                <table class="stl-table stl-table-compact">
                    <thead>
                        <tr>
                            <th>호</th>
                            <th class="num">사용량</th>
                            <th>관제점</th>
                            <th class="num">일수</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="row in rows" :key="row.sysNodeIdx" :class="{ 'row-warning': !row.ptMapped }">
                            <td class="ho">{{ row.roomName }}</td>
                            <td class="num">{{ fmtUsage(row.usageQty) }}</td>
                            <td class="nodeid microcap stl-rc-ptcell">
                                <template v-if="row.ptMapped">
                                    <template v-if="row.ptAddr">
                                        {{ row.ptAddr }}<span class="dim"> ({{ row.ptIdx }})</span>
                                    </template>
                                    <span v-else>({{ row.ptIdx }})</span>
                                </template>
                                <span v-else :title="L.ptUnmapped"
                                    ><i class="fa fa-exclamation-triangle" style="color: #a06d1a"></i> 미매핑</span
                                >
                            </td>
                            <td class="num microcap" :class="{ dim: row.dayCountComplete === false }">
                                {{ row.dayCount ?? "—" }}/{{ data.expectedDayCount ?? "—" }}
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </section>
    </div>
</template>

<script>
import { fmtUsage, unitLabel, statusLabel, SETTLEMENT_LABELS } from "../utils/settlementFormat.js";

export default {
    name: "ReconcileDetailPanel",
    props: {
        data: { type: Object, default: null },
        /** getHistoryMatrix 응답 — 스냅샷 M 비교용 */
        matrix: { type: Object, default: null },
    },
    data() {
        return { expanded: false };
    },
    computed: {
        L() {
            return SETTLEMENT_LABELS;
        },
        isGas() {
            return this.data?.energySource === "Gas" || this.data?.usageUnit === "Nm3";
        },
        unitText() {
            return unitLabel(this.data?.usageUnit || (this.isGas ? "Nm3" : "kWh"));
        },
        sourcesS() {
            return this.data?.sources?.S || {};
        },
        sourcesC() {
            return this.data?.sources?.C || null;
        },
        sourcesM() {
            return this.data?.sources?.M || {};
        },
        rows() {
            return this.data?.rows || [];
        },
        hasKepco() {
            return this.data?.kepcoKwh != null && this.data?.kepcoKwh !== undefined;
        },
        kepcoMatch() {
            if (!this.hasKepco) return false;
            return Math.abs(Number(this.data.kepcoDelta || 0)) < 0.0001;
        },
        kepcoDeltaText() {
            const d = Number(this.data?.kepcoDelta || 0);
            const sign = d > 0 ? "+" : "";
            return `${sign}${fmtUsage(d)} ${this.unitText}`;
        },
        kepcoDeltaClass() {
            if (this.kepcoMatch) return "stl-delta-flat";
            return Math.abs(Number(this.data?.kepcoDelta || 0)) > 100 ? "stl-delta-up" : "stl-delta-down";
        },
        snapshotUsage() {
            const v = this.matrix?.footer?.usageQty;
            return v != null ? Number(v) : null;
        },
        snapshotDelta() {
            if (this.snapshotUsage == null || !this.data) return null;
            return parseFloat((this.data.masterUsageQty - this.snapshotUsage).toFixed(4));
        },
        matrixStatus() {
            return this.matrix?.status || null;
        },
    },
    methods: {
        fmtUsage,
        statusLabel,
    },
};
</script>
